import { useEffect, useMemo, useState } from 'react';
import {
  cloneForcePreset,
  createForcePreset,
  deleteForcePreset,
  fetchFactionAssets,
  fetchFactionProfiles,
  fetchForcePresets,
  fetchLoadouts,
  updateForcePreset,
  type FactionAssets,
  type FactionProfileView,
  type ForcePresetBody,
  type ForcePresetMember,
  type ForcePresetView,
  type LoadoutView,
} from '../api/catalog';
import { ca } from '../i18n/ca';
import { AssetPicture } from './AssetPicture';
import { useConfirm } from './useConfirm';

/**
 * Unit and group presets: what a side is made of, saved once and picked
 * afterwards, on either side.
 *
 * The catalogue already held a loadout (one man's kit) and a faction profile (a
 * whole faction, as the probe read it). Between them there was nothing, so a
 * group had to be a `CfgGroups` class the game shipped — which is why every
 * element of every generated mission was one of Bohemia's own squads, and why
 * "our rifle section" or "the enemy's checkpoint detachment" could not be said
 * at all.
 *
 * A unit preset is one man: a class, a kit and a function. A group preset is a
 * list of men — each either a class or a unit preset followed into — and the
 * vehicles it arrives in. Nothing here reaches the game as a preset: the
 * platform flattens a group into a plain list of classes and kits when the
 * mission is published, so a mission is a fixed thing and a server that cannot
 * reach the platform still plays it.
 */

const SIDES = ['west', 'east', 'resistance', 'civilian'];
const ROLES = [
  'teamleader',
  'medic',
  'rto',
  'autorifleman',
  'at',
  'marksman',
  'engineer',
  'crew',
  'pilot',
  'rifleman',
];
const CATEGORIES = ['Infantry', 'Motorized', 'Mechanized', 'Armored', 'Support', 'Air', 'Naval'];

const c = ca.catalogPage2;

function parseBody(json: string): ForcePresetBody {
  try {
    return JSON.parse(json) as ForcePresetBody;
  } catch {
    return {};
  }
}

/** A draft being edited; `id` absent means it has never been saved. */
interface Draft {
  id?: number;
  kind: 'unit' | 'group';
  name: string;
  side: string;
  factionClass: string | null;
  body: ForcePresetBody;
}

const emptyDraft = (kind: 'unit' | 'group'): Draft => ({
  kind,
  name: '',
  side: 'east',
  factionClass: null,
  body:
    kind === 'unit' ? { unitClass: '', role: 'rifleman' } : { category: 'Infantry', members: [] },
});

export function ForcePresets({ modSetHash }: { modSetHash: string | null }) {
  const [confirm, confirmDialog] = useConfirm();
  const [presets, setPresets] = useState<ForcePresetView[]>([]);
  const [profiles, setProfiles] = useState<FactionProfileView[]>([]);
  const [loadouts, setLoadouts] = useState<LoadoutView[]>([]);
  const [assets, setAssets] = useState<FactionAssets | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useMemo(
    () => () => {
      if (!modSetHash) return;
      fetchForcePresets(modSetHash)
        .then(setPresets)
        .catch(() => setPresets([]));
    },
    [modSetHash],
  );

  useEffect(() => {
    reload();
    if (!modSetHash) return;
    fetchFactionProfiles(modSetHash)
      .then(setProfiles)
      .catch(() => setProfiles([]));
    fetchLoadouts(modSetHash)
      .then(setLoadouts)
      .catch(() => setLoadouts([]));
  }, [modSetHash, reload]);

  // The faction being edited decides which men and vehicles can be picked.
  useEffect(() => {
    const faction = draft?.factionClass;
    if (!modSetHash || !faction) {
      setAssets(null);
      return;
    }
    let live = true;
    fetchFactionAssets(modSetHash, faction)
      .then((a) => live && setAssets(a))
      .catch(() => live && setAssets(null));
    return () => {
      live = false;
    };
  }, [modSetHash, draft?.factionClass]);

  const units = presets.filter((p) => p.kind === 'UNIT');
  const groups = presets.filter((p) => p.kind === 'GROUP');

  function edit(p: ForcePresetView) {
    setDraft({
      id: p.id,
      kind: p.kind === 'UNIT' ? 'unit' : 'group',
      name: p.name,
      side: p.side,
      factionClass: p.factionClass,
      body: parseBody(p.bodyJson),
    });
  }

  async function save() {
    if (!modSetHash || !draft) return;
    setError(null);
    const input = {
      kind: draft.kind,
      name: draft.name,
      side: draft.side,
      factionClass: draft.factionClass,
      bodyJson: JSON.stringify(draft.body),
    };
    try {
      if (draft.id) await updateForcePreset(draft.id, input);
      else await createForcePreset(modSetHash, input);
      setDraft(null);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function remove(id: number) {
    if (
      !(await confirm({
        title: c.forceDeleteConfirm,
        confirmLabel: c.forceDelete,
        danger: true,
      }))
    ) {
      return;
    }
    await deleteForcePreset(id).catch(() => undefined);
    reload();
  }

  function setMember(i: number, patch: Partial<ForcePresetMember>) {
    setDraft((d) =>
      d
        ? {
            ...d,
            body: {
              ...d.body,
              members: (d.body.members ?? []).map((m, j) => (j === i ? { ...m, ...patch } : m)),
            },
          }
        : d,
    );
  }

  const list = (title: string, rows: ForcePresetView[], empty: string) => (
    <>
      <h3>{title}</h3>
      {rows.length === 0 && <p className="muted small">{empty}</p>}
      <ul className="list">
        {rows.map((p) => {
          const body = parseBody(p.bodyJson);
          const men =
            p.kind === 'GROUP' ? (body.members ?? []).reduce((n, m) => n + (m.count ?? 1), 0) : 1;
          return (
            <li key={p.id}>
              <span className="grow">
                <strong>{p.name}</strong>
                <span className={`chip side-${p.side}`}>{ca.sideNames[p.side] ?? p.side}</span>
                <span className="chip">{c.forceMen(men)}</span>
                {p.missing.length > 0 && (
                  <span className="chip warn">{c.forceMissing(p.missing.length)}</span>
                )}
              </span>
              <button type="button" onClick={() => edit(p)}>
                {c.forceEdit}
              </button>
              <button
                type="button"
                onClick={() =>
                  cloneForcePreset(p.id)
                    .then(reload)
                    .catch(() => undefined)
                }
              >
                {c.forceClone}
              </button>
              <button type="button" className="danger" onClick={() => remove(p.id)}>
                {c.forceDelete}
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );

  return (
    <section className="catalog-tab" aria-label={c.tabForces}>
      <p className="muted small">{c.forcesIntro}</p>
      {!modSetHash && <p className="muted small">{c.forceNeedsScan}</p>}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <p className="row">
        <button type="button" disabled={!modSetHash} onClick={() => setDraft(emptyDraft('unit'))}>
          {c.forceNewUnit}
        </button>
        <button type="button" disabled={!modSetHash} onClick={() => setDraft(emptyDraft('group'))}>
          {c.forceNewGroup}
        </button>
      </p>

      {draft && (
        <div className="card form" role="group" aria-label={c.forceNew}>
          <div className="two">
            <label>
              {c.forceName}
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label>
              {c.forceSide}
              <select
                value={draft.side}
                onChange={(e) => setDraft({ ...draft, side: e.target.value })}
              >
                {SIDES.map((s) => (
                  <option key={s} value={s}>
                    {ca.sideNames[s] ?? s}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            {c.forceFaction}
            <select
              value={draft.factionClass ?? ''}
              onChange={(e) => setDraft({ ...draft, factionClass: e.target.value || null })}
            >
              <option value="">{c.forceFactionAny}</option>
              {profiles
                .filter((p) => p.side === draft.side)
                .map((p) => (
                  <option key={p.id} value={p.factionClass}>
                    {p.name}
                  </option>
                ))}
            </select>
          </label>

          {draft.kind === 'unit' ? (
            <div className="two">
              <label>
                {c.forceUnitClass}
                <select
                  value={draft.body.unitClass ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, body: { ...draft.body, unitClass: e.target.value } })
                  }
                >
                  <option value="">—</option>
                  {(assets?.units ?? []).map((u) => (
                    <option key={u.className} value={u.className}>
                      {u.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {c.forceLoadout}
                <select
                  value={draft.body.loadoutId ?? ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      body: { ...draft.body, loadoutId: e.target.value || null },
                    })
                  }
                >
                  <option value="">{c.forceLoadoutNone}</option>
                  {loadouts.map((l) => (
                    <option key={l.id} value={String(l.id)}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
            <>
              <label>
                {c.forceCategory}
                <select
                  value={draft.body.category ?? 'Infantry'}
                  onChange={(e) =>
                    setDraft({ ...draft, body: { ...draft.body, category: e.target.value } })
                  }
                >
                  {CATEGORIES.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </label>
              <h4>{c.forceMembers}</h4>
              <div className="role-table">
                <div className="role-row head" aria-hidden="true">
                  <span />
                  <span>{c.forceMemberFrom}</span>
                  <span>{c.forceUnitClass}</span>
                  <span>{c.forceLoadout}</span>
                  <span>{c.forceRole}</span>
                  <span>{c.forceMemberCount}</span>
                  <span />
                </div>
                {(draft.body.members ?? []).map((m, i) => (
                  <div className="role-row" key={i}>
                    <AssetPicture
                      size={32}
                      assetClass={m.unitClass}
                      modSetHash={modSetHash}
                      picture={assets?.units.find((u) => u.className === m.unitClass)?.picture}
                      name={m.unitClass ?? undefined}
                    />
                    <select
                      aria-label={`${c.forceMemberFrom} ${i + 1}`}
                      value={m.unitPresetId ? String(m.unitPresetId) : ''}
                      onChange={(e) =>
                        setMember(i, {
                          unitPresetId: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                    >
                      <option value="">{c.forceMemberFromNone}</option>
                      {units.map((u) => (
                        <option key={u.id} value={String(u.id)}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                    <select
                      aria-label={`${c.forceUnitClass} ${i + 1}`}
                      value={m.unitClass ?? ''}
                      onChange={(e) => setMember(i, { unitClass: e.target.value || null })}
                      disabled={!!m.unitPresetId}
                    >
                      <option value="">—</option>
                      {(assets?.units ?? []).map((u) => (
                        <option key={u.className} value={u.className}>
                          {u.displayName}
                        </option>
                      ))}
                    </select>
                    <select
                      aria-label={`${c.forceLoadout} ${i + 1}`}
                      value={m.loadoutId ?? ''}
                      onChange={(e) => setMember(i, { loadoutId: e.target.value || null })}
                    >
                      <option value="">{c.forceLoadoutNone}</option>
                      {loadouts.map((l) => (
                        <option key={l.id} value={String(l.id)}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                    <select
                      aria-label={`${c.forceRole} ${i + 1}`}
                      value={m.role ?? ''}
                      onChange={(e) => setMember(i, { role: e.target.value || null })}
                    >
                      <option value="">—</option>
                      {ROLES.map((role) => (
                        <option key={role} value={role}>
                          {ca.missionUi.ours.slotRoleNames[role] ?? role}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label={`${c.forceMemberCount} ${i + 1}`}
                      type="number"
                      min={1}
                      max={24}
                      value={m.count ?? 1}
                      onChange={(e) =>
                        setMember(i, {
                          count: Math.max(1, Math.min(24, Number(e.target.value) || 1)),
                        })
                      }
                    />
                    <button
                      type="button"
                      aria-label={`${c.forceMemberRemove} ${i + 1}`}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          body: {
                            ...draft.body,
                            members: (draft.body.members ?? []).filter((_, j) => j !== i),
                          },
                        })
                      }
                    >
                      <i className="bi bi-x-lg" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="row">
                <button
                  type="button"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      body: {
                        ...draft.body,
                        members: [
                          ...(draft.body.members ?? []),
                          { unitClass: assets?.units[0]?.className ?? '', count: 1 },
                        ],
                      },
                    })
                  }
                >
                  {c.forceMemberAdd}
                </button>
              </p>
              <h4>{c.forceVehicles}</h4>
              <ul className="list">
                {(draft.body.vehicles ?? []).map((v, i) => (
                  <li key={i}>
                    <select
                      aria-label={`${c.forceVehicles} ${i + 1}`}
                      value={v.className}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          body: {
                            ...draft.body,
                            vehicles: (draft.body.vehicles ?? []).map((x, j) =>
                              j === i ? { ...x, className: e.target.value } : x,
                            ),
                          },
                        })
                      }
                    >
                      {(assets?.vehicles ?? []).map((x) => (
                        <option key={x.className} value={x.className}>
                          {x.displayName}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          body: {
                            ...draft.body,
                            vehicles: (draft.body.vehicles ?? []).filter((_, j) => j !== i),
                          },
                        })
                      }
                    >
                      <i className="bi bi-x-lg" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={!assets || assets.vehicles.length === 0}
                onClick={() =>
                  setDraft({
                    ...draft,
                    body: {
                      ...draft.body,
                      vehicles: [
                        ...(draft.body.vehicles ?? []),
                        { className: assets!.vehicles[0].className, count: 1 },
                      ],
                    },
                  })
                }
              >
                {c.forceVehicleAdd}
              </button>
            </>
          )}

          <p className="row">
            <button type="button" className="primary" onClick={save}>
              {c.forceSave}
            </button>
            <button type="button" onClick={() => setDraft(null)}>
              {c.forceCancel}
            </button>
          </p>
        </div>
      )}

      {list(c.forceUnits, units, c.forceEmptyUnits)}
      {list(c.forceGroups, groups, c.forceEmptyGroups)}
      {confirmDialog}
    </section>
  );
}
